import { Mail } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useTranslation } from '@/components/i18n/Translator';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { findMessageElement, processMessageElementForRichText } from '@/utils/htmlProcessing';

interface Props {
  content: unknown;
  className?: string;
  messageId?: string;
}



const EmailButton = ({ content, className, messageId }: Props) => {
  const [isOpening, setIsOpening] = useState(false);
  const { t } = useTranslation();

  const openEmailWithContent = async () => {
    try {
      setIsOpening(true);

      const plainTextContent =
        typeof content === 'object'
          ? JSON.stringify(content, null, 2)
          : String(content);

      // First, copy rich text to clipboard (same logic as CopyButton)
      let copiedSuccessfully = false;

      // Try to copy rich text (HTML) if messageId is provided
      if (messageId && navigator.clipboard.write) {
        try {
          const messageElement = findMessageElement(messageId, 'EmailButton');
          
          if (messageElement) {
            const htmlContent = processMessageElementForRichText(messageElement, 'EmailButton');
            
            // Create clipboard item with both HTML and plain text
            const clipboardItem = new ClipboardItem({
              'text/html': new Blob([htmlContent], { type: 'text/html' }),
              'text/plain': new Blob([plainTextContent], { type: 'text/plain' })
            });
            
            await navigator.clipboard.write([clipboardItem]);
            console.log('EmailButton: Successfully copied rich text to clipboard');
            copiedSuccessfully = true;
          } else {
            console.log('EmailButton: No messageElement found, falling back to plain text');
          }
        } catch (htmlError) {
          console.warn('Rich text copy failed, falling back to plain text:', htmlError);
        }
      }

      // Fallback to plain text copy if rich text failed
      if (!copiedSuccessfully) {
        await navigator.clipboard.writeText(plainTextContent);
      }

      // Show alert that content has been copied
      alert('Content has been copied to clipboard - please paste it in your email application.');

      // Open email client with empty body but pre-filled subject
      const subject = encodeURIComponent('Response from Signature Aviation Assistant');
      const mailtoURL = `mailto:?subject=${subject}`;
      window.open(mailtoURL, '_self');

      // Reset state after a short delay
      setTimeout(() => {
        setIsOpening(false);
      }, 1000);
    } catch (err) {
      setIsOpening(false);
      toast.error('Failed to copy content or open email: ' + String(err));
    }
  };

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={openEmailWithContent}
            variant="ghost"
            size="icon"
            className={`text-muted-foreground ${className}`}
            disabled={isOpening}
          >
            <Mail className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {isOpening
              ? 'Opening email...'
              : 'Email this response'}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default EmailButton;